// Minimal fetch-based HTTP client that mirrors the axios subset previously in
// use. Adds the Rails CSRF token automatically and parses JSON responses by
// content-type, returning text otherwise.
//
// Returns an object with a `data` field so existing call sites that read
// `response.data` keep working unchanged.

const csrfMetaTag = document.head.querySelector('[name="csrf-token"]');
const csrfToken = csrfMetaTag?.getAttribute("content") ?? "";

export interface RequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
}

export interface HttpResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

function buildUrl(url: string, params?: Record<string, unknown>): string {
  if (!params) return url;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  if (!qs) return url;
  return url + (url.includes("?") ? "&" : "?") + qs;
}

async function request<T>(
  method: string,
  url: string,
  config?: RequestConfig,
): Promise<HttpResponse<T>> {
  const finalUrl = buildUrl(url, config?.params);
  const headers: Record<string, string> = {
    "X-CSRF-TOKEN": csrfToken,
    ...(config?.headers ?? {}),
  };

  const response = await fetch(finalUrl, { method, headers });
  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`);
    (error as Error & { status?: number; response?: Response }).status =
      response.status;
    (error as Error & { status?: number; response?: Response }).response =
      response;
    throw error;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json")
    ? ((await response.json()) as T)
    : ((await response.text()) as unknown as T);

  return { data, status: response.status, headers: response.headers };
}

const http = {
  get: <T = unknown>(url: string, config?: RequestConfig) =>
    request<T>("GET", url, config),
  delete: <T = unknown>(url: string, config?: RequestConfig) =>
    request<T>("DELETE", url, config),
};

export default http;
